import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { CookieService } from "ngx-cookie-service";
import { first } from "rxjs/operators";

import { environment } from "src/environments/environment";

import { RankingsModel } from "../models/goals.model";
import { SalesModel } from "../models/sales.model";

type RankingPreset =
  | "current_month"
  | "previous_month"
  | "same_period_last_year"
  | "ytd"
  | "custom";
type GoalPreviewScope = "entity" | "store" | "consultant";
type AdvancedRankingScope = "consultants" | "directors" | "agencies";
type AdvancedRankingMetric = "sales" | "listings";
type AdvancedComparePreset = "none" | "previous_month" | "same_period_last_year";
type AdvancedSortDirection = "asc" | "desc";

@Injectable({
  providedIn: "root",
})
export class SalesProcessingService {
  constructor(private http: HttpClient, private cookieService: CookieService) { }

  private formatDateParam(date: Date) {
    const value = new Date(date);
    const year = value.getFullYear();
    const month = `${value.getMonth() + 1}`.padStart(2, "0");
    const day = `${value.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  createExternalSale(data: any) {
    return this.http
      .post(`${environment.apiUrl}/sales-processing/external-sale`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  getAgentSales(dates: any, page: any) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang).set("page", page);
    if (dates.to && dates.from) {
      let from = this.formatDateParam(dates.from);
      params = params.set("from", from);
      const to = this.formatDateParam(dates.to);
      params = params.set("to", to);
    }
    return this.http
      .get<any>(`${environment.apiUrl}/sales-processing/agent`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  getGoals() {
    return this.http
      .get<any>(`${environment.apiUrl}/sales-processing/goals`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  getGoal(
    id: number,
    options?: {
      scope?: GoalPreviewScope;
      storeId?: number | string | null;
      consultantId?: string | null;
    },
  ) {
    let params = new HttpParams();
    if (options?.scope) {
      params = params.set("scope", options.scope);
    }
    if (options?.storeId) {
      params = params.set("storeId", `${options.storeId}`);
    }
    if (options?.consultantId) {
      params = params.set("consultantId", `${options.consultantId}`);
    }
    return this.http
      .get<any>(`${environment.apiUrl}/sales-processing/${id}/goal`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  saveGoal(data: {
    year: any;
    val: any;
    scope?: GoalPreviewScope;
    storeId?: number | null;
    consultantId?: string | null;
  }) {
    return this.http
      .post(`${environment.apiUrl}/sales-processing/goal`, data, {
        withCredentials: true,
      })
      .pipe(first());
  }

  getFilters() {
    const lang = this.cookieService.get("lang");

    let params = new HttpParams().set("lang", lang);
    return this.http
      .get<any>(`${environment.apiUrl}/sales-processing/filters`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  getAllAgents(dates: any, page: any, filters: any) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang).set("page", page);
    if (dates.to && dates.from) {
      let from = this.formatDateParam(dates.from);
      params = params.set("from", from);
      const to = this.formatDateParam(dates.to);
      params = params.set("to", to);
    }
    if (filters) {
      params = params.set("filters", JSON.stringify(filters));
    }
    return this.http
      .get<any>(`${environment.apiUrl}/sales-processing`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  getSales(dates: any, page: any, filters: any, search: string) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang).set("page", page);
    if (dates.to && dates.from) {
      let from = this.formatDateParam(dates.from);
      params = params.set("from", from);
      const to = this.formatDateParam(dates.to);
      params = params.set("to", to);
    }
    if (filters) {
      params = params.set("filters", JSON.stringify(filters));
    }
    if (search) params = params.set("search", search);
    return this.http
      .get<{ sales: SalesModel[], total: number, totals?: { all: number, sale: number, rent: number } }>(`${environment.apiUrl}/sales-processing`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  getExternalSales(dates: any, page: any) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang).set("page", page);
    if (dates.to && dates.from) {
      let from = this.formatDateParam(dates.from);
      params = params.set("from", from);
      const to = this.formatDateParam(dates.to);
      params = params.set("to", to);
    }
    return this.http
      .get<any>(`${environment.apiUrl}/sales-processing/external-sales`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  getRankings(
    dates: any,
    page: any,
    lang?: string,
    entityId?: number | string,
    preset: RankingPreset = "current_month",
  ) {
    const effectiveLang = lang || this.cookieService.get("lang");

    let params = new HttpParams()
      .set("lang", effectiveLang)
      .set("page", page)
      .set("preset", preset);

    if (entityId) {
      params = params.set("entityId", `${entityId}`);
    }

    if (dates?.to && dates?.from) {
      const from = this.formatDateParam(dates.from);
      params = params.set("from", from);
      const to = this.formatDateParam(dates.to);
      params = params.set("to", to);
    }

    return this.http
      .get<RankingsModel>(`${environment.apiUrl}/sales-processing/rankings`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  getAdvancedRanking(payload: {
    entityId: number | string;
    preset: RankingPreset;
    dates?: any;
    page?: number;
    pageSize?: number;
    scope?: AdvancedRankingScope;
    metric?: AdvancedRankingMetric;
    compareTo?: AdvancedComparePreset;
    completedOnly?: boolean;
    sortBy?: string;
    sortDirection?: AdvancedSortDirection;
  }) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams()
      .set("lang", lang)
      .set("entityId", `${payload.entityId}`)
      .set("preset", payload.preset)
      .set("page", `${payload.page ?? 1}`)
      .set("pageSize", `${payload.pageSize ?? 50}`)
      .set("scope", payload.scope ?? "consultants")
      .set("metric", payload.metric ?? "sales")
      .set("compareTo", payload.compareTo ?? "none")
      .set("completedOnly", payload.completedOnly ? "true" : "false")
      .set("sortBy", payload.sortBy ?? "")
      .set("sortDirection", payload.sortDirection ?? "desc");

    if (payload?.dates?.to && payload?.dates?.from) {
      const from = this.formatDateParam(payload.dates.from);
      const to = this.formatDateParam(payload.dates.to);
      params = params.set("from", from).set("to", to);
    }

    return this.http
      .get<any>(`${environment.apiUrl}/performance/rankings/advanced`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  exportAdvancedRanking(payload: {
    entityId: number | string;
    preset: RankingPreset;
    dates?: any;
    scope?: AdvancedRankingScope;
    metric?: AdvancedRankingMetric;
    compareTo?: AdvancedComparePreset;
    completedOnly?: boolean;
    sortBy?: string;
    sortDirection?: AdvancedSortDirection;
  }) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams()
      .set("lang", lang)
      .set("entityId", `${payload.entityId}`)
      .set("preset", payload.preset)
      .set("scope", payload.scope ?? "consultants")
      .set("metric", payload.metric ?? "sales")
      .set("compareTo", payload.compareTo ?? "none")
      .set("completedOnly", payload.completedOnly ? "true" : "false")
      .set("sortBy", payload.sortBy ?? "")
      .set("sortDirection", payload.sortDirection ?? "desc");

    if (payload?.dates?.to && payload?.dates?.from) {
      const from = this.formatDateParam(payload.dates.from);
      const to = this.formatDateParam(payload.dates.to);
      params = params.set("from", from).set("to", to);
    }

    return this.http
      .get(`${environment.apiUrl}/performance/rankings/advanced/export`, {
        params,
        responseType: "blob",
        withCredentials: true,
      })
      .pipe(first());
  }

  getAdvancedRankingDrilldown(payload: {
    entityId: number | string;
    preset: RankingPreset;
    rowId: string | number;
    column: string;
    dates?: any;
    scope?: AdvancedRankingScope;
    metric?: AdvancedRankingMetric;
    completedOnly?: boolean;
  }) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams()
      .set("lang", lang)
      .set("entityId", `${payload.entityId}`)
      .set("preset", payload.preset)
      .set("scope", payload.scope ?? "consultants")
      .set("metric", payload.metric ?? "sales")
      .set("completedOnly", payload.completedOnly ? "true" : "false")
      .set("rowId", `${payload.rowId}`)
      .set("column", payload.column);

    if (payload?.dates?.to && payload?.dates?.from) {
      const from = this.formatDateParam(payload.dates.from);
      const to = this.formatDateParam(payload.dates.to);
      params = params.set("from", from).set("to", to);
    }

    return this.http
      .get<any>(`${environment.apiUrl}/performance/rankings/advanced/drilldown`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  getAgentSalesById(dates: any, page: any, id: any) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang).set("page", page);
    if (dates.to && dates.from) {
      let from = this.formatDateParam(dates.from);
      params = params.set("from", from);
      const to = this.formatDateParam(dates.to);
      params = params.set("to", to);
    }
    return this.http
      .get<any>(`${environment.apiUrl}/sales-processing/agent/${id}`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  getEntitySales(dates: any, page: any) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang).set("page", page);
    if (dates.to && dates.from) {
      let from = this.formatDateParam(dates.from);
      params = params.set("from", from);
      const to = this.formatDateParam(dates.to);
      params = params.set("to", to);
    }
    return this.http
      .get<any>(`${environment.apiUrl}/sales-processing/entity`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  cancelCommissions(property: any, type: any) {
    const data = {
      property,
      type,
    };
    // let params = new HttpParams().set('property', property).set('agent', agent);
    return this.http
      .patch<any>(
        `${environment.apiUrl}/sales-processing/cancel-commissions`,
        { data: data },
        { withCredentials: true }
      )
      .pipe(first());
  }

  payment(property: any, type: any) {
    const data = {
      property,
      type,
    };
    return this.http
      .patch<any>(
        `${environment.apiUrl}/sales-processing/payment`,
        { data: data },
        { withCredentials: true }
      )
      .pipe(first());
  }

  getExternalById(id: number) {
    return this.http
      .get<any>(`${environment.apiUrl}/sales-processing/external-sale/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  updateExternalSale(id: number, data: any) {
    return this.http
      .patch(
        `${environment.apiUrl}/sales-processing/external-sale/${id}`,
        { data: data },
        { withCredentials: true }
      )
      .pipe(first());
  }

  deleteExternal(id: number) {
    return this.http
      .delete(`${environment.apiUrl}/sales-processing/external-sale/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  deleteGoal(id: number) {
    return this.http
      .delete(`${environment.apiUrl}/sales-processing/goal/${id}`, {
        withCredentials: true,
      })
      .pipe(first());
  }

  exportRankingData(
    type: string,
    dates: any,
    agentId?: any,
    entityId?: number | string,
    preset: RankingPreset = "current_month",
  ) {
    const lang = this.cookieService.get("lang");

    let params = new HttpParams()
      .set("lang", lang)
      .set("type", type)
      .set("preset", preset);

    if (entityId) {
      params = params.set("entityId", `${entityId}`);
    }

    if (dates?.to && dates?.from) {
      let from = this.formatDateParam(dates.from);
      params = params.set("from", from);
      const to = this.formatDateParam(dates.to);
      params = params.set("to", to);
    }

    if (agentId) {
      params = params.set("agentId", agentId);
    }

    return this.http
      .get(`${environment.apiUrl}/sales-processing/rankings/export`, {
        params,
        responseType: "blob",
        withCredentials: true,
      })
      .pipe(first());
  }

  exportPerformanceReport(
    report:
      | "recruitment"
      | "sales"
      | "listings"
      | "transactions"
      | "leads"
      | "global"
      | "stores"
      | "consultants"
      | "directors"
      | "sales-processing",
    dates: any,
    entityId?: number | string,
    preset: RankingPreset = "current_month",
    storeId?: number | null,
    consultantId?: string | null,
  ) {
    const lang = this.cookieService.get("lang");

    let params = new HttpParams()
      .set("lang", lang)
      .set("report", report)
      .set("preset", preset);

    if (entityId) {
      params = params.set("entityId", `${entityId}`);
    }

    if (storeId) {
      params = params.set("storeId", `${storeId}`);
    }

    if (consultantId) {
      params = params.set("consultantId", `${consultantId}`);
    }

    if (dates?.to && dates?.from) {
      const from = this.formatDateParam(dates.from);
      const to = this.formatDateParam(dates.to);
      params = params.set("from", from).set("to", to);
    }

    return this.http
      .get(`${environment.apiUrl}/performance/export`, {
        params,
        responseType: "blob",
        withCredentials: true,
      })
      .pipe(first());
  }

  getPerformanceReportView(
    report:
      | "recruitment"
      | "sales"
      | "listings"
      | "transactions"
      | "leads"
      | "global"
      | "stores"
      | "consultants"
      | "directors"
      | "sales-processing",
    dates: any,
    entityId?: number | string,
    preset: RankingPreset = "current_month",
    storeId?: number | null,
    consultantId?: string | null,
  ) {
    const lang = this.cookieService.get("lang");

    let params = new HttpParams()
      .set("lang", lang)
      .set("report", report)
      .set("preset", preset);

    if (entityId) {
      params = params.set("entityId", `${entityId}`);
    }

    if (storeId) {
      params = params.set("storeId", `${storeId}`);
    }

    if (consultantId) {
      params = params.set("consultantId", `${consultantId}`);
    }

    if (dates?.to && dates?.from) {
      const from = this.formatDateParam(dates.from);
      const to = this.formatDateParam(dates.to);
      params = params.set("from", from).set("to", to);
    }

    return this.http
      .get<any>(`${environment.apiUrl}/performance/reports/view`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  getPerformanceOverview(
    profile:
      | "master"
      | "store_director"
      | "commercial_director"
      | "consultant",
    dates: any,
    entityId?: number | string,
    preset: RankingPreset = "current_month",
    focus?: { storeId?: number | null; consultantId?: string | null },
  ) {
    const lang = this.cookieService.get("lang");

    let params = new HttpParams()
      .set("lang", lang)
      .set("profile", profile)
      .set("preset", preset);

    if (entityId) {
      params = params.set("entityId", `${entityId}`);
    }

    if (focus?.storeId) {
      params = params.set("storeId", `${focus.storeId}`);
    }

    if (focus?.consultantId) {
      params = params.set("consultantId", `${focus.consultantId}`);
    }

    if (dates?.to && dates?.from) {
      const from = this.formatDateParam(dates.from);
      const to = this.formatDateParam(dates.to);
      params = params.set("from", from).set("to", to);
    }

    return this.http
      .get<any>(`${environment.apiUrl}/performance/kpis/overview`, {
        params,
        withCredentials: true,
      })
      .pipe(first());
  }

  confirmTransaction(id: number, data: any) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang)
    return this.http
      .patch<SalesModel>(
        `${environment.apiUrl}/sales-processing/confirm-transaction/${id}`,
        data,
        { params, withCredentials: true }
      )
      .pipe(first());
  }

  confirmPayment(id: number, data: any) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang)
    return this.http
      .patch<SalesModel>(
        `${environment.apiUrl}/sales-processing/confirm-payment/${id}`,
        data,
        { params, withCredentials: true }
      )
      .pipe(first());
  }

  revertTransaction(id: number) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang)
    return this.http
      .patch<SalesModel>(
        `${environment.apiUrl}/sales-processing/revert-transaction/${id}`,
        {},
        { params, withCredentials: true }
      )
      .pipe(first());
  }

  revertCommission(id: number) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang)
    return this.http
      .patch<SalesModel>(
        `${environment.apiUrl}/sales-processing/revert-commission/${id}`,
        {},
        { params, withCredentials: true }
      )
      .pipe(first());
  }

  setSaleStage(
    id: number,
    data: {
      type: "RESERVATION" | "SIGNAL" | "SALE_AGREEMENT" | "DEED";
      order?: number;
      eventDate?: any;
      confirmTransaction?: boolean;
      action?: "SET" | "ADVANCE";
    },
  ) {
    const lang = this.cookieService.get("lang");
    const params = new HttpParams().set("lang", lang);
    return this.http
      .patch<SalesModel>(
        `${environment.apiUrl}/sales-processing/set-stage/${id}`,
        data,
        { params, withCredentials: true },
      )
      .pipe(first());
  }

  updateInProgressSaleDetails(id: number, data: any) {
    const lang = this.cookieService.get("lang");
    const params = new HttpParams().set("lang", lang);
    return this.http
      .patch<SalesModel>(
        `${environment.apiUrl}/sales-processing/in-progress-details/${id}`,
        data,
        { params, withCredentials: true },
      )
      .pipe(first());
  }

  completeSale(id: number) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang)
    return this.http
      .patch<SalesModel>(
        `${environment.apiUrl}/sales-processing/complete-sale/${id}`,
        {},
        { params, withCredentials: true }
      )
      .pipe(first());
  }

  cancelSale(id: number) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang)
    return this.http
      .patch<SalesModel>(
        `${environment.apiUrl}/sales-processing/cancel-sale/${id}`,
        {},
        { params, withCredentials: true }
      )
      .pipe(first());
  }

  updateTransaction(id: number, data: any) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang)
    return this.http
      .patch<SalesModel>(
        `${environment.apiUrl}/sales-processing/update-transaction/${id}`,
        data,
        { params, withCredentials: true }
      )
      .pipe(first());
  }

  updateCommission(id: number, data: any) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang)
    return this.http
      .patch<SalesModel>(
        `${environment.apiUrl}/sales-processing/update-commission/${id}`,
        data,
        { params, withCredentials: true }
      )
      .pipe(first());
  }

  cancelTransaction(id: number) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang)
    return this.http
      .patch<SalesModel>(
        `${environment.apiUrl}/sales-processing/cancel-transaction/${id}`,
        {},
        { params, withCredentials: true }
      )
      .pipe(first());
  }

  cancelCommission(id: number) {
    const lang = this.cookieService.get("lang");
    let params = new HttpParams().set("lang", lang)
    return this.http
      .patch<SalesModel>(
        `${environment.apiUrl}/sales-processing/cancel-commission/${id}`,
        {},
        { params, withCredentials: true }
      )
      .pipe(first());
  }

  deleteSale(id: number) {
    return this.http
      .delete<SalesModel>(
        `${environment.apiUrl}/sales-processing/${id}`,
        { withCredentials: true }
      )
      .pipe(first());
  }

  previewCommissionTarget(data: any) {
    return this.http
      .post<any>(
        `${environment.apiUrl}/sales-processing/commission-target-preview`,
        data,
        { withCredentials: true },
      )
      .pipe(first());
  }
}
